import { Box } from '@mui/material';
import LogRocket from 'logrocket';
import { type ComponentType, type FC, type LazyExoticComponent, lazy } from 'react';

import type { KeysOfType } from '@core/utils/types';

import { ErrorPage } from '../components/Error/ErrorPage';
import { LoadingSpinner } from '../components/LoadingSpinner/LoadingSpinner';

const LoadingPage: FC = () => (
  <Box
    height='60vh'
    display='flex'
    flexDirection='column'
    gap='3rem'
    justifyContent='center'
    alignItems='center'
    pt='20vh'
  >
    <LoadingSpinner />
  </Box>
);

function lazyWithRetry<
  M extends Record<string, any>,
  K extends KeysOfType<M, ComponentType<any>>,
  T extends M[K],
>(
  importFn: () => Promise<M>, // Supports named exports
  exportName: K, // Specify the named export
  retries: number = 3,
  baseDelay: number = 500, // Base delay in milliseconds
): LazyExoticComponent<T> {
  const attemptImport = async (
    remainingRetries: number,
    attempt: number = 1,
  ): Promise<{ default: T }> => {
    try {
      const module = await importFn();

      if (!(exportName in module)) {
        throw new Error(`Module does not contain named export: ${exportName}`);
      }

      return { default: module[exportName] }; // Convert named export to default
    } catch (err) {
      // Render error page if URL contains 'retry' query parameter
      if (new URLSearchParams(window.location.search).has('retry')) {
        return { default: ErrorPage as T };
      }

      // Recur
      if (remainingRetries > 0) {
        // Exponential backoff: baseDelay * 2^(attempt - 1)
        const delay = baseDelay * 2 ** (attempt - 1);
        await new Promise((resolve) => {
          setTimeout(resolve, delay);
        });
        return attemptImport(remainingRetries - 1, attempt + 1);
      }

      window.location.href = `${window.location.protocol}//${window.location.host}${
        window.location.pathname
      }?retry`;

      LogRocket.warn(err);
      return {
        default: LoadingPage as T,
      };
    }
  };

  return lazy(() => attemptImport(retries));
}

export const Dashboard = lazyWithRetry(() => import('../pages/Dashboard/Dashboard'), 'Dashboard');

export const ActivitiesView = lazyWithRetry(
  () => import('../pages/Activities/ActivitiesView'),
  'ActivitiesView',
);

export const ActivityDetailsPage = lazyWithRetry(
  () => import('../pages/Activities/ActivityDetails'),
  'ActivityDetailsPage',
);

export const AdminPage = lazyWithRetry(() => import('../pages/Admin/AdminPage'), 'AdminPage');

export const ApprovalFlowPage = lazyWithRetry(
  () => import('../pages/Admin/QuoteApprovalFlow/ApprovalFlowPage'),
  'ApprovalFlowPage',
);

export const SystemRemindersList = lazyWithRetry(
  () => import('../pages/Admin/SystemReminders/SystemRemindersList'),
  'SystemRemindersList',
);

export const AdminUserManagementPage = lazyWithRetry(
  () => import('../pages/Admin/AdminUserManagementTabsPage/AdminUserManagementTabsPage'),
  'AdminUserManagementTabsPage',
);

export const CompanyDetailsPage = lazyWithRetry(
  () => import('../pages/Companies/CompanyDetails'),
  'CompanyDetailsPage',
);

export const CompanyList = lazyWithRetry(
  () => import('../pages/Companies/CompanyList'),
  'CompanyList',
);

export const DispatchCustomerList = lazyWithRetry(
  () => import('../pages/Companies/DispatchCustomers/DispatchCustomerList'),
  'DispatchCustomerList',
);

export const CompetitorsList = lazyWithRetry(
  () => import('../pages/Competitors/CompetitorsList/CompetitorsList'),
  'CompetitorsList',
);

export const ContactDetailsPage = lazyWithRetry(
  () => import('../pages/Contacts/ContactDetails'),
  'ContactDetailsPage',
);

export const ContactList = lazyWithRetry(
  () => import('../pages/Contacts/ContactList'),
  'ContactList',
);

export const ForecastList = lazyWithRetry(
  () => import('../pages/Forecasts/ForecastList'),
  'ForecastList',
);

export const MaterialDetailsPage = lazyWithRetry(
  () => import('../pages/Materials/MaterialDetails'),
  'MaterialDetailsPage',
);

export const MaterialList = lazyWithRetry(
  () => import('../pages/Materials/MaterialList'),
  'MaterialList',
);

export const MixDetailsPage = lazyWithRetry(
  () => import('../pages/MixDesigns/MixDesignDetails'),
  'MixDetailsPage',
);

export const MixList = lazyWithRetry(() => import('../pages/MixDesigns/MixDesignList'), 'MixList');

export const EmailConfigLayout = lazyWithRetry(
  () => import('../pages/EmailConfig/EmailConfigLayout'),
  'EmailConfigLayout',
);

export const EmailAccountPage = lazyWithRetry(
  () => import('../pages/EmailConfig/EmailAccountPage'),
  'EmailAccountPage',
);

export const EmailSignaturesPage = lazyWithRetry(
  () => import('../pages/EmailConfig/EmailSignaturesPage'),
  'EmailSignaturesPage',
);

export const EmailCcBccPage = lazyWithRetry(
  () => import('../pages/EmailConfig/EmailCcBccPage'),
  'EmailCcBccPage',
);

export const NotificationPreferencesPage = lazyWithRetry(
  () => import('../pages/NotificationPreferences/NotificationPreferencesPage'),
  'NotificationPreferencesPage',
);

export const PlantDetailsPage = lazyWithRetry(
  () => import('../pages/Plants/PlantDetails'),
  'PlantDetailsPage',
);

export const PlantList = lazyWithRetry(() => import('../pages/Plants/PlantList'), 'PlantList');

export const ProductDetailsPage = lazyWithRetry(
  () => import('../pages/Products/ProductDetails'),
  'ProductDetailsPage',
);

export const ProductList = lazyWithRetry(
  () => import('../pages/Products/ProductList'),
  'ProductList',
);

export const ProjectConfigDetailsPage = lazyWithRetry(
  () => import('../pages/ProjectConfigs/ProjectConfigDetails'),
  'ProjectConfigDetailsPage',
);

export const ProjectConfigList = lazyWithRetry(
  () => import('../pages/ProjectConfigs/ProjectConfigList'),
  'ProjectConfigList',
);

export const ProjectDetailsPage = lazyWithRetry(
  () => import('../pages/Projects/ProjectDetails/ProjectDetails'),
  'ProjectDetailsPage',
);

export const ProjectList = lazyWithRetry(
  () => import('../pages/Projects/ProjectList/ProjectList'),
  'ProjectList',
);

export const QuoteConfigDetailsPage = lazyWithRetry(
  () => import('../pages/QuoteConfigs/QuoteConfigDetails'),
  'QuoteConfigDetailsPage',
);

export const QuoteConfigList = lazyWithRetry(
  () => import('../pages/QuoteConfigs/QuoteConfigList'),
  'QuoteConfigList',
);

export const QuoteDetailsPage = lazyWithRetry(
  () => import('../pages/Quotes/QuoteDetails/QuoteDetails'),
  'QuoteDetailsPage',
);

export const QuoteList = lazyWithRetry(
  () => import('../pages/Quotes/QuoteList/QuoteList'),
  'QuoteList',
);

export const EscalationManagerList = lazyWithRetry(
  () => import('../pages/Quotes/EscalationManager/EscalationManagerList'),
  'EscalationManagerList',
);

export const MarginConfiguration = lazyWithRetry(
  () => import('../pages/Admin/MarginConfiguration/MarginConfiguration'),
  'MarginConfiguration',
);

export const StatusConfiguration = lazyWithRetry(
  () => import('../pages/Admin/QuoteStatusConfiguration/QuoteStatusConfiguration'),
  'StatusConfiguration',
);

export const ActualsPage = lazyWithRetry(
  () => import('../pages/Actuals/ActualsPage'),
  'ActualsPage',
);

export const ProjectSettings = lazyWithRetry(
  () => import('../pages/Admin/ProjectSettings/ProjectSettings'),
  'ProjectSettings',
);

export const BacklogSettings = lazyWithRetry(
  () => import('../pages/Admin/BacklogSettings/BacklogSettings'),
  'BacklogSettings',
);
export const PlanningPage = lazyWithRetry(
  () => import('../pages/Planning/PlanningPage'),
  'PlanningPage',
);

export const TierPricingSettings = lazyWithRetry(
  () => import('../pages/Admin/TierPricingSettings/TierPricingSettings'),
  'TierPricingSettings',
);

export const TenantConfiguration = lazyWithRetry(
  () => import('../pages/Admin/TenantConfiguration/TenantConfiguration'),
  'TenantConfiguration',
);

export const TierPricingPage = lazyWithRetry(
  () => import('../pages/TierPricing/TierPricingPage'),
  'TierPricingPage',
);

export const QuotePDFTemplateConfiguration = lazyWithRetry(
  () => import('../pages/Admin/QuotePDFTemplateConfiguration/QuotePDFTemplateConfiguration'),
  'QuotePDFTemplateConfiguration',
);

export const Refer = lazyWithRetry(() => import('../pages/ReferPage/ReferPage'), 'ReferPage');

export const AIChatPage = lazyWithRetry(() => import('../pages/AIChat/AIChatPage'), 'AIChatPage');

export const TruckingList = lazyWithRetry(
  () => import('../pages/Trucking/TruckingList'),
  'TruckingList',
);
